import { Injectable } from '@angular/core';
import { Asset } from '@gelato-api-ui/core/designs/asset';
import { AssetFileType } from '@gelato-api-ui/core/designs/asset-file-type.enum';
import { AssetProviderGroupId } from '@gelato-api-ui/core/designs/asset-provider-group-id.enum';
import { AssetProviderUid } from '@gelato-api-ui/core/designs/asset-provider-uid.enum';
import { AssetsCollection } from '@gelato-api-ui/core/designs/assets-collection';
import { freeAssetProviderUidsWithActiveSubscription } from '@gelato-api-ui/core/designs/free-asset-provider-uids-for-subscribers';
import { getAssetFileUrl } from '@gelato-api-ui/core/designs/helpers/getAssetFileUrl';
import { PaidAssetProviderUids } from '@gelato-api-ui/core/designs/paid-asset-provider-uids';
import { UploadedImageColorsProvider } from '@gelato-api-ui/core/designs/services/uploaded-image-colors.provider';
import { AssetMetadataKey } from '@gelato-api-ui/core/designs/types/asset-metadata-key.enum';
import { UploadedImageData } from '@gelato-api-ui/core/designs/uploaded-image-data';
import { TranslateService } from '@ngx-translate/core';
import { AssetDigitizationStatus } from '../asset-digitization-status.enum';

@Injectable({
  providedIn: 'root',
})
export class UploadedImageDataService {
  constructor(
    private readonly uploadedImageColorsProvider: UploadedImageColorsProvider,
    private readonly translations: TranslateService,
  ) {}

  getUploadedImageDataFromAsset(
    id: string,
    asset: Asset,
    isGelatoPlusGoldActive = false,
    useOriginalIfNoDefault = false,
  ): UploadedImageData {
    const isAssetHidden = asset.metadata?.find(
      meta => meta.key === AssetMetadataKey.BACKGROUND_REMOVAL_HAS_BACKGROUND && !meta.value,
    );

    let url = getAssetFileUrl(asset, AssetFileType.PREVIEW_DEFAULT);
    if (!url && useOriginalIfNoDefault) {
      url = getAssetFileUrl(asset, AssetFileType.FILE_ORIGINAL);
    }

    return {
      file: {
        id,
        width: asset.width,
        height: asset.height,
        label: this.getAssetLabel(asset, isGelatoPlusGoldActive),
        source: asset.assetProviderUid,
        colors: this.uploadedImageColorsProvider.get(asset),
        quantizedColors: this.uploadedImageColorsProvider.getQuantized(asset),
        tooltip: this.getAssetTooltip(asset),
      },
      url,
      quantizedUrl: getAssetFileUrl(asset, AssetFileType.PREVIEW_QUANTIZED),
      thumbnailUrl: getAssetFileUrl(asset, AssetFileType.PREVIEW_THUMBNAIL),
      mimeType: asset.mimeType,
      assetId: asset.id,
      hidden: Boolean(isAssetHidden),
    };
  }

  mapAssetsToUploadedImagesData(
    assets: Asset[],
    gelatoPlusOrGoldIsActive = false,
    useOriginalIfNoDefault = false,
  ): UploadedImageData[] {
    if (!assets || !assets.length) {
      return [];
    }

    return assets.map(loopAsset =>
      this.getUploadedImageDataFromAsset(loopAsset.id, loopAsset, gelatoPlusOrGoldIsActive, useOriginalIfNoDefault),
    );
  }

  getUploadedImagesDataFromAssetsCollection(
    assetsCollection: AssetsCollection,
    gelatoPlusOrGoldIsActive: boolean,
  ): UploadedImageData[] {
    return Object.keys(assetsCollection).map(assetId => {
      const loopAsset = assetsCollection[assetId];
      return this.getUploadedImageDataFromAsset(loopAsset.id, loopAsset, gelatoPlusOrGoldIsActive);
    });
  }

  private isAssetDigitizing(asset: Asset): boolean {
    return [AssetDigitizationStatus.DIGITIZED, AssetDigitizationStatus.DIGITIZING].includes(asset.digitizationStatus);
  }

  private getAssetLabel(asset: Asset, isGelatoPlusGoldActive: boolean): string {
    if (this.isAssetDigitizing(asset)) {
      return this.translations.instant('txt_asset_digitized');
    }

    const isGettyEssentialAsset =
      asset.assetProviderUid === AssetProviderUid.GETTY_IMAGES &&
      asset.providerAssetGroupUid === AssetProviderGroupId.GETTY_IMAGES_ESSENTIAL;

    if (isGelatoPlusGoldActive && isGettyEssentialAsset) {
      return null;
    }

    const hideLabel =
      isGelatoPlusGoldActive && freeAssetProviderUidsWithActiveSubscription.includes(asset.assetProviderUid);
    if (hideLabel) {
      return null;
    }

    return PaidAssetProviderUids.includes(asset.assetProviderUid) ? '$' : null;
  }

  private getAssetTooltip(asset: Asset): string {
    if (this.isAssetDigitizing(asset)) {
      return this.translations.instant('txt_asset_is_alread_digitized');
    }

    return undefined;
  }
}
